import React, { useState, useCallback } from "react";
import { Eye, EyeOff } from "react-feather";
import {
  Box,
  Typography,
  TextField,
  Button,
  // Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Lock } from "react-feather";

const Container = styled(Box)(({ theme }) => ({
  height: "100vh",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
}));

const FormSection = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6),
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100%",
}));

const MainContent = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "345px",
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.spacing(6),
  padding: theme.spacing(4, 0),

  [theme.breakpoints.up("md")]: {
    maxWidth: "400px",
    gap: theme.spacing(8),
    padding: theme.spacing(6, 0),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    display: "flex",
    height: "48px",
    padding: "8px 12px",
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "12px",
    border: "1px solid rgba(0, 0, 0, 0.03)",
    background: "rgba(0, 0, 0, 0.05)",
    "& fieldset": {
      border: "none",
    },
    "&:hover": {
      border: "1px solid rgba(0, 0, 0, 0.08)",
    },
    "&.Mui-focused": {
      border: "1px solid rgba(0, 0, 0, 0.12)",
    },
    "&.Mui-error": {
      border: "1px solid rgba(244, 67, 54, 0.5)",
    },
    // Hide any default password visibility icons
    "& .MuiInputAdornment-root:has(button[aria-label*='password'])": {
      display: "none",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "16px",
    padding: "0",
    height: "auto",
    "&::placeholder": {
      color: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
    },
    // Hide browser's default password reveal button
    "&::-ms-reveal": {
      display: "none",
    },
    "&::-webkit-credentials-auto-fill-button": {
      display: "none",
    },
  },
  "& .MuiFormHelperText-root": {
    fontSize: "12px",
    marginLeft: 0,
    marginTop: "4px",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  height: 48,
  borderRadius: 28,
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 500,
  [theme.breakpoints.up("sm")]: {
    height: 56,
  },
  "&.MuiButton-contained": {
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  "&.MuiButton-text": {
    padding: theme.spacing(1, 2),
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Aeonik, Inter, sans-serif",
  "&.title": {
    fontSize: "32px",
    fontWeight: 500,
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: {
      fontSize: "40px",
    },
  },
  "&.subtitle": {
    fontSize: "16px",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  "&.label": {
    fontSize: "12px",
    fontWeight: 400,
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: {
      fontSize: "14px",
    },
  },
}));

// const StyledAlert = styled(Alert)(({ theme }) => ({
//   borderRadius: theme.spacing(1),
//   fontSize: "12px",
//   "& .MuiAlert-message": {
//     fontSize: "12px",
//   },
// }));

interface PasswordProtectedProps {
  onPasswordVerified: () => void;
  verifyPassword: (password: string) => Promise<boolean>;
}

function PasswordProtected({
  onPasswordVerified,
  verifyPassword,
}: PasswordProtectedProps) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTogglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
      setError(null);
    },
    []
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!password.trim()) {
        return;
      }

      try {
        setIsSubmitting(true);
        setError(null);

        const isValid = await verifyPassword(password);
        if (isValid) {
          onPasswordVerified();
        } else {
          setError("Incorrect password. Please try again.");
        }
      } catch (err) {
        setError("An error occurred. Please try again.");
        console.error("Password verification error:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [password, verifyPassword, onPasswordVerified]
  );

  return (
    <Container>
      <FormSection>
        <ScrollableContent>
          <MainContent>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "16px auto",
                }}
              >
                <Lock size={80} />
              </Box>
              <StyledTypography className="title" sx={{ textAlign: "center" }}>
                Enter your password to continue
              </StyledTypography>
            </Box>

            {/* {error && <StyledAlert severity="error">{error}</StyledAlert>} */}

            <Box display="flex" flexDirection="column" gap={8}>
              <Box
                id="form"
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box>
                  <StyledTextField
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={handlePasswordChange}
                    error={Boolean(error)}
                    helperText={error || ""}
                    disabled={isSubmitting}
                    fullWidth
                    autoComplete="current-password"
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          sx={{
                            color: "rgba(0, 0, 0, 0.5)",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "rgba(0, 0, 0, 0.7)",
                            },
                          }}
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>

                <StyledButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting || !password.trim()}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Continue"
                  )}
                </StyledButton>
              </Box>
            </Box>
          </MainContent>
        </ScrollableContent>
      </FormSection>
    </Container>
  );
}

export default PasswordProtected;
